'use client';
import { forwardRef, useImperativeHandle, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Input as InputPrimitive } from './shadcn/input';
import { interpolate, useTranslation } from '../../lib/i18n';

const LENGTH = 6;
const SLOTS = Array.from({ length: LENGTH }, (_, index) => index);

export type OneTimeCodeInputHandle = { focus: () => void };

type OneTimeCodeInputProps = {
  /** The digits entered so far, contiguous from the first box; a complete code has six. */
  value: string;
  onChange: (value: string) => void;
  /** Called when an edit writes the sixth box of a full code (typing it, a paste, autofill); overtyping an
   *  earlier box of a full code does not submit a half-old code. Callers that need a second field leave it out. */
  onComplete?: (code: string) => void;
  /** Accessible name of the whole group; each box adds its own "digit n of 6". */
  label: string;
  invalid?: boolean;
  disabled?: boolean;
  'aria-describedby'?: string;
};

const digitsOf = (text: string) => text.replace(/\D/g, '');

/** Six-box one-time-code entry. Every box is a real numeric field with `autocomplete="one-time-code"`, so
 *  an SMS or authenticator suggestion fills whichever box has focus; a value longer than one digit (that
 *  autofill, a paste, Android keyboards that send no key events) is spread from that box onwards. The value
 *  has no holes: focusing a box past the first empty one moves to the first empty one. */
export const OneTimeCodeInput = forwardRef<OneTimeCodeInputHandle, OneTimeCodeInputProps>(function OneTimeCodeInput(
  { value, onChange, onComplete, label, invalid, disabled, 'aria-describedby': describedBy },
  ref,
) {
  const { t } = useTranslation();
  const boxes = useRef<Array<HTMLInputElement | null>>([]);
  // Focus moves in the same handler that changes the value, before React re-renders with it, so the focus
  // guard reads the value just written rather than the one this render closed over.
  const latest = useRef(value);
  latest.current = value;
  const update = (next: string) => { latest.current = next; onChange(next); };
  const focusBox = (index: number) => {
    const box = boxes.current[Math.max(0, Math.min(index, LENGTH - 1))];
    box?.focus();
    box?.select();
  };
  useImperativeHandle(ref, () => ({ focus: () => focusBox(latest.current.length) }), []);

  function commit(next: string, focusAt: number) {
    update(next);
    focusBox(focusAt);
    if (next.length === LENGTH && focusAt >= LENGTH) onComplete?.(next);
  }

  /** Writes `digits` from box `index`; a whole code replaces everything instead. */
  function enter(index: number, digits: string) {
    if (!digits) return;
    if (digits.length >= LENGTH) { commit(digits.slice(0, LENGTH), LENGTH); return; }
    const next = (value.slice(0, index) + digits + value.slice(index + digits.length)).slice(0, LENGTH);
    commit(next, index + digits.length);
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      // A filled box clears itself; an empty one steps back and clears the previous digit.
      const target = index < value.length ? index : index - 1;
      if (target < 0) return;
      update(value.slice(0, target) + value.slice(target + 1));
      focusBox(target);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(Math.min(index + 1, value.length));
    }
  }

  function handlePaste(index: number, event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    enter(index, digitsOf(event.clipboardData.getData('text')));
  }

  return (
    <div role="group" aria-label={label} aria-describedby={describedBy} data-slot="one-time-code" className="flex gap-2">
      {SLOTS.map(index => (
        <InputPrimitive
          key={index}
          ref={(node) => { boxes.current[index] = node; }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]"
          aria-label={interpolate(t.common.codeDigit, { index: index + 1, count: LENGTH })}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          value={value[index] ?? ''}
          // The shared Input owns the field's type step (`text-ui`, `text-body` under a finger so iOS never
          // zooms in), so a digit box states no size of its own.
          className="w-10 px-0 text-center tabular-nums"
          onFocus={(event) => { if (index > latest.current.length) focusBox(latest.current.length); else event.currentTarget.select(); }}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onChange={(event) => {
            const raw = event.target.value;
            // Keyboards that send no Backspace key event (Android) arrive here as an emptied box.
            if (raw === '') { update(value.slice(0, index) + value.slice(index + 1)); return; }
            // Autofill either replaces the box or appends to its digit; the code is the last six digits.
            if (digitsOf(raw).length >= LENGTH) { enter(index, digitsOf(raw).slice(-LENGTH)); return; }
            // A box holds one digit, so with it unselected the input sits around the caret beside that digit.
            const caret = event.target.selectionStart ?? raw.length;
            const inserted = value[index] !== undefined && raw.length > 1 ? raw.slice(caret - (raw.length - 1), caret) : raw;
            enter(index, digitsOf(inserted));
          }}
        />
      ))}
    </div>
  );
});
